import { MediaController } from 'media-chrome';
import { MediaUIEvents } from 'media-chrome/dist/constants.js';
import { isIOS } from '../utils/browser-detection.js';
import {
  CAPTIONS_OFF,
  patchSettings,
  type PlayerSettings,
  readSettings,
} from '../utils/player-prefs.ts';

const NUMERIC_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const ATTR_PLAYBACK_RATES = 'playbackrates' as const;

/** Which group of preferences a pending write covers. */
type PrefWriteKind = 'captions' | 'audio' | 'speed';

const PREF_WRITE_EVENTS: Array<[string, PrefWriteKind]> = [
  [MediaUIEvents.MEDIA_SHOW_SUBTITLES_REQUEST, 'captions'],
  [MediaUIEvents.MEDIA_DISABLE_SUBTITLES_REQUEST, 'captions'],
  [MediaUIEvents.MEDIA_TOGGLE_SUBTITLES_REQUEST, 'captions'],
  [MediaUIEvents.MEDIA_MUTE_REQUEST, 'audio'],
  [MediaUIEvents.MEDIA_UNMUTE_REQUEST, 'audio'],
  [MediaUIEvents.MEDIA_VOLUME_REQUEST, 'audio'],
  [MediaUIEvents.MEDIA_PLAYBACK_RATE_REQUEST, 'speed'],
];

/**
 * Custom media controller component that extends Media Chrome's controller with additional keyboard shortcuts
 *
 * Adds support for numeric key shortcuts (0-9) to seek to specific video positions:
 * - 0: Seek to 0% of video duration
 * - 1: Seek to 10% of video duration
 * - 2: Seek to 20% of video duration
 * ...
 * - 9: Seek to 90% of video duration
 *
 * @attr {string} playbackrates - Space-separated list of playback rates (e.g., "0.5 0.75 1 1.25 2")
 */
export class BunnyMediaController extends MediaController {
  // iOS native fullscreen handlers: when playsinline=false, iOS uses its own
  // fullscreen player. On dismissal (swipe down), iOS shows the video inline
  // with native controls. We hide custom controls to prevent overlapping UI.
  #iosFullscreenBeginHandler = (e: Event) => {
    if (e.target !== this.media) return;
    this.removeAttribute('data-ios-native-ui');
  };

  #iosFullscreenEndHandler = (e: Event) => {
    const mediaEl = e.target as HTMLElement | null;
    if (!mediaEl || mediaEl !== this.media) return;

    if (
      !mediaEl.hasAttribute('playsinline') &&
      !mediaEl.hasAttribute('webkit-playsinline')
    ) {
      this.setAttribute('data-ios-native-ui', '');
    }
  };

  #numericKeyHandler = (e: KeyboardEvent) => {
    const { metaKey, altKey, ctrlKey, shiftKey, key, target } = e;

    // Only handle numeric keys without modifiers
    if (
      metaKey || altKey || ctrlKey || shiftKey || !NUMERIC_KEYS.includes(key)
    ) {
      return;
    }

    // Ignore if typing in editable fields
    const el = target as HTMLElement | null;
    const isEditable = !!el && (
      el.tagName === 'INPUT' ||
      el.tagName === 'TEXTAREA' ||
      (el as HTMLElement).isContentEditable
    );
    if (isEditable) return;

    const percentage = parseInt(key, 10) * 10; // Convert key to percentage (0-90)
    const duration = this.mediaStore.getState().mediaDuration;

    if (duration) {
      const targetTime = (duration * percentage) / 100;
      e.preventDefault();
      this.mediaStore?.dispatch({
        type: MediaUIEvents.MEDIA_SEEK_REQUEST,
        detail: targetTime,
      });
    }
  };

  constructor() {
    super();
    // Add our numeric key handler
    this.addEventListener('keydown', this.#numericKeyHandler);

    // Listen for media control events to ensure we get focus back
    this.addEventListener(MediaUIEvents.MEDIA_PLAY_REQUEST, () => {
      requestAnimationFrame(() => this.focus());
    });
    this.addEventListener(MediaUIEvents.MEDIA_PAUSE_REQUEST, () => {
      requestAnimationFrame(() => this.focus());
    });

    // Persist viewer preferences from the request events the UI dispatches.
    //
    // These must be DOM listeners, not observation of `mediasubtitlesshowing` and
    // friends: that state also changes for reasons that are not viewer intent — the
    // server default-track activation, the iOS fullscreen handler forcing
    // mode='showing', and MediaController.disconnectedCallback dispatching "disable all
    // subtitles" on any DOM move. Those all go through mediaStore.dispatch(), which
    // emits no DOM event, so a plain listener here sees only real UI and hotkey intent.
    for (const [eventName, kind] of PREF_WRITE_EVENTS) {
      this.addEventListener(eventName, () => this.#queuePrefWrite(kind));
    }
  }

  #pendingPrefWrites = new Set<PrefWriteKind>();
  #prefWriteScheduled = false;
  #playbackRateTouched = false;

  #queuePrefWrite(kind: PrefWriteKind): void {
    // Once the viewer picks a speed, the deferred initial rate below must not undo it.
    if (kind === 'speed') this.#playbackRateTouched = true;

    this.#pendingPrefWrites.add(kind);
    if (this.#prefWriteScheduled) return;

    // Coalesce, and read the outcome rather than the request detail: the captions menu
    // fires disable+show as a synchronous pair, the `c` hotkey fires only a toggle, and
    // an unmute request bumps volume to 0.25 when it was 0.
    //
    // A microtask, not requestAnimationFrame — rAF never fires while the iframe is
    // hidden or offscreen, which is exactly the LMS-carousel case this fixes.
    this.#prefWriteScheduled = true;
    queueMicrotask(() => {
      this.#prefWriteScheduled = false;
      this.#flushPrefWrites();
    });
  }

  #flushPrefWrites(): void {
    const kinds = this.#pendingPrefWrites;
    this.#pendingPrefWrites = new Set();

    const media = this.media;
    if (!media || kinds.size === 0) return;
    // While casting, volume belongs to the TV rather than to this viewer.
    if (this.hasAttribute('data-casting')) return;

    const patch: PlayerSettings = {};

    if (kinds.has('captions')) {
      // Read track modes directly: the TextTrackList `change` event fires in a task, so
      // the media store's mediaSubtitlesShowing is still stale inside this microtask.
      const showing = Array.from(media.textTracks).find((track) =>
        (track.kind === 'subtitles' || track.kind === 'captions') &&
        track.mode === 'showing'
      );
      if (!showing) {
        patch.captions = CAPTIONS_OFF;
      } else if (showing.language) {
        patch.captions = showing.language.toLowerCase();
      }
      // A showing track with no language tells us nothing; leave the stored value alone.
    }

    if (kinds.has('audio')) {
      patch.muted = media.muted;
      patch.volume = media.volume;
    }

    if (kinds.has('speed')) {
      patch.speed = media.playbackRate;
    }

    if (Object.keys(patch).length > 0) {
      patchSettings(this, patch);
    }
  }

  /**
   * Apply stored volume and mute. Restores set properties directly rather than
   * dispatching requests, so they never loop back into #queuePrefWrite.
   */
  #restoreAudioPreferences(media: HTMLMediaElement): void {
    const settings = readSettings(this);

    if (settings.volume !== undefined) {
      media.volume = settings.volume;
    }

    // A saved mute is always safe to apply. A saved *unmute* is not: `?muted=true`
    // renders the muted attribute for autoplay, and unmuting without a user gesture is
    // blocked on Safari, so the video would silently fail to start. The embed wins.
    if (settings.muted === true) {
      media.muted = true;
    } else if (settings.muted === false && !media.hasAttribute('muted')) {
      media.muted = false;
    }
  }

  static override get observedAttributes(): string[] {
    return [...super.observedAttributes, ATTR_PLAYBACK_RATES];
  }

  override connectedCallback(): void {
    super.connectedCallback();
    // Ensure the controller is focusable
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback?.();
    // Clean up our event listener
    this.removeEventListener('keydown', this.#numericKeyHandler);
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ): void {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (oldValue === newValue) return;

    if (name === ATTR_PLAYBACK_RATES && this.media) {
      this.#initializePlaybackRate(this.media);
    }
  }

  override mediaSetCallback(media: HTMLMediaElement): void {
    super.mediaSetCallback(media);
    this.#restoreAudioPreferences(media);
    // Initialize playback rate if attribute is already set
    if (this.hasAttribute(ATTR_PLAYBACK_RATES)) {
      this.#initializePlaybackRate(media);
    }
    // On iOS, track native fullscreen state to hide custom controls
    // when the video exits native fullscreen without playsinline
    if (isIOS()) {
      media.addEventListener(
        'webkitbeginfullscreen',
        this.#iosFullscreenBeginHandler,
      );
      media.addEventListener(
        'webkitendfullscreen',
        this.#iosFullscreenEndHandler,
      );
    }
  }

  override mediaUnsetCallback(media: HTMLMediaElement): void {
    super.mediaUnsetCallback(media);
    media.removeEventListener(
      'webkitbeginfullscreen',
      this.#iosFullscreenBeginHandler,
    );
    media.removeEventListener(
      'webkitendfullscreen',
      this.#iosFullscreenEndHandler,
    );
    this.removeAttribute('data-ios-native-ui');
  }

  #initializePlaybackRate(media: HTMLMediaElement): void {
    const playbackRates = this.getAttribute(ATTR_PLAYBACK_RATES);
    if (!playbackRates) return;

    // Parse rates (space-separated string)
    const rates = playbackRates
      .split(/\s+/)
      .map((r) => parseFloat(r))
      .filter((r) => !isNaN(r));

    if (rates.length === 0) return;

    // Prefer the viewer's saved speed when this video actually offers it, otherwise
    // 1.0 or the closest value to it.
    // createEmbedConfig forces showSpeed off for live, so player.vto omits the rate
    // menu - restoring a speed saved on a VOD would leave the stream running fast with
    // no control to undo it.
    const savedSpeed = this.getAttribute('data-live-stream') === 'true'
      ? undefined
      : readSettings(this).speed;
    const targetRate = (savedSpeed !== undefined && rates.includes(savedSpeed))
      ? savedSpeed
      : rates.find((r) => r === 1.0) ||
        rates.reduce(
          (closest, current) =>
            Math.abs(current - 1.0) < Math.abs(closest - 1.0)
              ? current
              : closest,
          rates[0],
        );

    // Set initial playback rate when media is ready
    const setInitialRate = () => {
      // The viewer changed speed while the media was still loading; leave their choice.
      if (this.#playbackRateTouched) {
        media.removeEventListener('loadedmetadata', setInitialRate);
        media.removeEventListener('loadeddata', setInitialRate);
        return;
      }
      if (media.readyState >= 1) {
        this.mediaStore?.dispatch({
          type: MediaUIEvents.MEDIA_PLAYBACK_RATE_REQUEST,
          detail: targetRate,
        });
        media.removeEventListener('loadedmetadata', setInitialRate);
        media.removeEventListener('loadeddata', setInitialRate);
      }
    };

    if (media.readyState >= 1) {
      setInitialRate();
    } else {
      media.addEventListener('loadedmetadata', setInitialRate, { once: true });
      media.addEventListener('loadeddata', setInitialRate, { once: true });
    }
  }

  // Public API
  get playbackRates(): string | null {
    return this.getAttribute(ATTR_PLAYBACK_RATES);
  }

  set playbackRates(value: string | null) {
    if (value) {
      this.setAttribute(ATTR_PLAYBACK_RATES, value);
    } else {
      this.removeAttribute(ATTR_PLAYBACK_RATES);
    }
  }
}

// Register the custom element if it hasn't been registered yet
if (!globalThis.customElements.get('media-controller')) {
  globalThis.customElements.define('media-controller', BunnyMediaController);
}

export default BunnyMediaController;
